import React, { useState, useEffect } from "react";
import { Modal, Button, Row, Col } from "antd";
import { LeftOutlined, RightOutlined, LinkOutlined, CloseOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./hotelcarousel.css";
import Pricing from "../Pricing/pricing";
import StarRating from "../../../StarRating/starrating"; // Adjust the path as needed
import { useTranslation } from "react-i18next";
import WhatsAppIcon from "../../../../assets/images/icons/whatsapp.png";
import EmailIcon from "../../../../assets/images/icons/email.png";

// Hotel carousel component to browse through different hotels
const HotelCarousel = ({ 
  hotels = [], 
  visible, 
  onClose, 
  initialIndex = 0, 
  totalKids,
  totalRooms,
  totalAdults,
  numberOfNights,
  currentClient,
  searchSession
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Reset current index when hotels array or initialIndex changes
  useEffect(() => {
    if (hotels && hotels.length > 0) {
      // Make sure initialIndex is within bounds
      const validIndex = Math.max(0, Math.min(initialIndex, hotels.length - 1));
      setCurrentIndex(validIndex);
    }
  }, [hotels, initialIndex, visible]);

  // Navigate to previous hotel
  const handlePrev = () => {
    if (hotels.length <= 1) return;
    
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? hotels.length - 1 : prevIndex - 1
    );
  };

  // Navigate to next hotel
  const handleNext = () => {
    if (hotels.length <= 1) return;
    
    setCurrentIndex((prevIndex) => 
      prevIndex === hotels.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Navigate to hotel details page
  const goToHotelDetails = () => {
    if (hotels[currentIndex]) {
      navigate(`/hotelprofile/${hotels[currentIndex].ID}`);
      onClose();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      handleNext(); // In RTL interface, left arrow moves to next
    } else if (e.key === 'ArrowRight') {
      handlePrev(); // In RTL interface, right arrow moves to previous
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    if (visible) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, currentIndex]);

  // If no hotels or not visible, don't render anything
  if (!hotels || hotels.length === 0 || !visible) {
    return null;
  }

  const currentHotel = hotels[currentIndex];
  if (!currentHotel) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      centered
      className="hotel-carousel-modal"
      closeIcon={<CloseOutlined />}
    >
      <div className="hotel-carousel-container">
        <Row className="tw-w-full tw-h-full">
          {/* Hotel Image */}
          <Col xs={24} md={16} className="tw-relative">
            <div className="hotel-image-container">
              <img
                src={currentHotel.DefaultImage?.FullSize || '/default-hotel.jpg'}
                alt={currentHotel.DisplayName}
                className="carousel-image"
              />
              
              {/* Navigation buttons - only shown if there are multiple hotels */}
              {hotels.length > 1 && (
                <>
                  <div className="carousel-nav-left">
                    <Button
                      shape="circle"
                      onClick={handlePrev}
                      icon={<LeftOutlined />}
                      className="carousel-nav-button"
                    />
                  </div>
                  
                  <div className="carousel-nav-right">
                    <Button
                      shape="circle"
                      onClick={handleNext}
                      icon={<RightOutlined />}
                      className="carousel-nav-button"
                    />
                  </div>
                  
                  {/* Counter indicator */}
                  <div className="carousel-counter">
                    {currentIndex + 1} / {hotels.length}
                  </div>
                </>
              )}
            </div>
          </Col>
          
          {/* Hotel Info */}
          <Col xs={24} md={8} className="tw-p-4">
            <div className="hotel-info">
              <h2 className="tw-text-xl tw-font-bold tw-mb-2">
                {currentHotel.DisplayName}
              </h2>
              
              <div className="hotel-details tw-mb-4">
                {currentHotel.StarRating && (
                  <div className="hotel-stars tw-mb-2">
                    <StarRating rating={currentHotel.StarRating} />
                  </div>
                )}
                
                <div className="hotel-address tw-text-gray-600 tw-mb-2">
                  {currentHotel.Address}
                </div>
                
                {currentHotel.distance && (
                  <div className="hotel-distance tw-text-gray-600">
                    {currentHotel.distance.toFixed(2)} ק"מ מהיעד
                  </div>
                )}
              </div>

              
              <span className="hotelObject-text">
                  {numberOfNights} {t("hotelsearchbar.nights")} {totalRooms}{" "}
                  {t("hotelsearchbar.rooms")} {totalAdults}{" "}
                  {t("hotelsearchbar.adults")}{" "}
                  {totalKids > 0
                    ? `${totalKids} ${t("hotelsearchbar.kids")}`
                    : ""}
                </span>
                <div className="hotelObject-share">
              {/* Share options */}
              <div className="hotelObject-icon-wrapper">
                {currentClient?.phone && (
                  <a
                    href={`https://wa.me/${currentClient.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="hotelObject-icon"
                      src={WhatsAppIcon}
                      alt="Whatsapp Icon"
                    />
                  </a>
                )}

                {currentClient?.email && (
                  <a href={`mailto:${currentClient.email}`}>
                    <img
                      className="hotelObject-icon"
                      src={EmailIcon}
                      alt="Email Icon"
                    />
                  </a>
                )}
              </div>
            </div>

                <div className="hotelObject-pricing">
                  <Pricing item={currentHotel} searchSession={searchSession} />
                </div>
            </div>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default HotelCarousel;